import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ExistingProvider,
  forwardRef,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Type,
  ChangeDetectionStrategy
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import * as _ from 'lodash-es';
import {BehaviorSubject, combineLatest, fromEvent, merge, NEVER} from 'rxjs';
import {finalize, switchMap, takeUntil, tap} from 'rxjs/operators';
import {BehaviorSubjectSkipSame} from '../../common/rxjs/behavior-subject-skip-same';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {destroy$} from '../destroyable';
import {ModalService} from '../modal';
import {SelectInputMenuComponent} from './select-input-menu';
import {SelectItem} from './select-item';

const SELECT_INPUT_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectInputComponent),
    multi: true
};

const IS_SELECT_OPEN_CLASS = '-open';
const IS_SELECT_EMPTY_CLASS = '-empty';
const IS_SELECT_DISABLED_CLASS = '-disabled';
const IS_SELECT_HOVERED_CLASS = '-hover';
const IS_SELECT_HAS_CLEAR_CONTROL_CLASS = '-has-clear-control';

interface DisplayRec {
    label: string;
    labelLong: string;
}

@Component({
    selector: 'select-input',
    templateUrl: './select-input.component.html',
    styleUrls: ['./select-input.component.scss'],
    providers: [SELECT_INPUT_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectInputComponent implements ControlValueAccessor, OnInit, OnChanges {

    @Input()
    public selectMenuItems: SelectItem[];

    @Input()
    public placeholder: string;

    @HostBinding(`class.${IS_SELECT_OPEN_CLASS}`)
    public selectInputMenuComponent: SelectInputMenuComponent;

    @Input()
    public set labelElement(labelElement: HTMLElement) {
        this.labelElement$.next(labelElement);
    }

    /**
     * Displays the control in the right, that allows to clear the input.
     */
    @Input()
    public showClearBtn: boolean = false;

    @HostBinding(`class.${IS_SELECT_EMPTY_CLASS}`)
    public get isEmpty(): boolean {
        return !this.selectItem;
    }

    @HostBinding(`class.${IS_SELECT_DISABLED_CLASS}`)
    @HostBinding('attr.disabled')
    public isDisabled: boolean = null; // Null is required in order to remove "disabled" attribute.

    @HostBinding(`class.${IS_SELECT_HOVERED_CLASS}`)
    public isLabelHovered: boolean = false;

    @HostBinding(`class.${IS_SELECT_HAS_CLEAR_CONTROL_CLASS}`)
    public isClearBtnVisible: boolean = false;

    public readonly destroy$ = destroy$();

    public displayRec: DisplayRec = {
        label:     '',
        labelLong: ''
    };

    protected selectItem: SelectItem;

    private outerPropagateOnChange: (value: any) => void;

    private labelElement$ = new BehaviorSubject<HTMLElement>(null);

    private ngControl: NgControl;

    private readonly disabled$ = new BehaviorSubjectSkipSame<boolean>(false);

    constructor(
        private elementRef: ElementRef,
        private modalService: ModalService,
        private injector: Injector,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    public writeValue(value: any): void {
        const newSelectItem: SelectItem = this.getSelectItemByValue(value);

        this.setSelectItem(newSelectItem);
        this.updateCalculatedFields();
    }

    public propagateChange(value: any): void {
        if (this.outerPropagateOnChange) {
            this.outerPropagateOnChange(value);
        }

        this.updateCalculatedFields();
    }

    public registerOnChange(handler: (value: any) => void): void {
        this.outerPropagateOnChange = handler;
    }

    public registerOnTouched(handler: (value: any) => void): void {
        // Not implemented.
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled || null; // Null is required in order to remove "disabled" attribute.
        this.disabled$.next(true);
    }

    public ngOnInit(): void {
        this.ngControl = this.injector.get<NgControl>(NgControl as Type<NgControl>, undefined, {self: true});

        this.updateCalculatedFields();
        this.listenToLabelEvents();
    }

    public ngOnChanges(changes: TypedSimpleChanges<SelectInputComponent>): void {
        if (changes.selectMenuItems && this.ngControl) {
            this.writeValue(this.ngControl.value);
        }

        if (changes.isDisabled) {
            this.disabled$.next(this.isDisabled);
        }

        if (changes.showClearBtn) {
            this.updateCalculatedFields();
        }
    }

    @HostListener('click', ['$event'])
    public setListeners(event: MouseEvent): boolean {
        if (!this.isDisabled) {
            event.stopPropagation();
            event.preventDefault();

            this.openDropdownMenu();
        }

        return false;
    }

    public clear(event: MouseEvent): void {
        const EMPTY_VALUE: any = null;

        event.stopPropagation();

        this.submitValue(EMPTY_VALUE);
    }

    protected submitValue(value: any): void {
        const newSelectItem = (this.selectItem !== value)
            ? this.getSelectItemByValue(value)
            : this.selectItem;

        if (this.selectItem !== newSelectItem) {
            this.setSelectItem(newSelectItem);
            this.propagateChange(value);
        }
    }

    protected getSelectItemByValue(value: any, createOptionIfUnknown: boolean = true): SelectItem {
        return this.selectMenuItems.find(item => item.value === value)
            || (createOptionIfUnknown ? {value, label: String(value || '')} : null);
    }

    protected setSelectItem(newSelectItem: SelectItem): void {
        this.selectItem = newSelectItem;
        this.displayRec = {
            label:     newSelectItem && newSelectItem.label || '',
            labelLong: newSelectItem && (newSelectItem.labelLong || newSelectItem.label) || ''
        };

        this.changeDetectorRef.markForCheck();
    }

    private toggleDropdownMenu(): void {
        if (!this.selectInputMenuComponent) {
            this.openDropdownMenu();
        } else {
            this.modalService.close(this.selectInputMenuComponent);
            this.onCloseDropdownMenu();
        }
    }

    private openDropdownMenu(): void {
        if (!this.selectInputMenuComponent) {
            this.selectInputMenuComponent = this.modalService.open(SelectInputMenuComponent, {
                configure: {
                    targetElement:   this.elementRef,
                    menuItems:       this.selectMenuItems,
                    onCloseCallback: this.onCloseDropdownMenu.bind(this)
                }
            }).instance;

            this.changeDetectorRef.markForCheck();
        }
    }

    private onCloseDropdownMenu(value?: any): void {
        this.selectInputMenuComponent = null;

        if (value !== undefined) {
            this.submitValue(value);
        }

        this.changeDetectorRef.markForCheck();
    }

    private listenToLabelEvents(): void {
        const listenToClick = (element: HTMLElement) =>
            fromEvent(element, 'click').pipe(
                tap(() => this.toggleDropdownMenu())
            );

        const setIsLabelHovered = (value: boolean) => {
            this.isLabelHovered = value;
            this.changeDetectorRef.markForCheck();
        };

        const listenToHover = (element: HTMLElement) => merge(
            fromEvent(element, 'mouseenter').pipe(
                tap(() => setIsLabelHovered(true))
            ),
            fromEvent(element, 'mouseleave').pipe(
                tap(() => setIsLabelHovered(false))
            )
        ).pipe(
            finalize(() => setIsLabelHovered(false))
        );

        combineLatest([
            this.labelElement$,
            this.disabled$
        ]).pipe(
            switchMap(([element, isDisabled]) => (element && !isDisabled)
                ? merge(
                    listenToClick(element),
                    listenToHover(element)
                )
                : NEVER
            ),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateCalculatedFields(): void {
        this.isClearBtnVisible = !!(this.showClearBtn && this.ngControl && !_.isNil(this.ngControl.value));
    }
}
